import { supabase } from '../lib/supabase';

// Case conversion helpers
const toSnakeCase = (obj) => {
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
};

const toCamelCase = (obj) => {
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
};

// Niche-specific data templates
const nicheTemplates = {
  hvac: {
    services: ['AC Installation', 'Furnace Repair', 'Duct Cleaning', 'Thermostat Installation', 'HVAC Maintenance', 'Air Quality Testing', 'Heat Pump Service', 'Emergency AC Repair'],
    contactTitles: ['Facility Manager', 'Property Owner', 'Building Superintendent', 'Homeowner', 'Business Owner'],
    dealNames: ['Commercial HVAC Upgrade', 'Residential AC Installation', 'Annual Maintenance Contract', 'Emergency Furnace Replacement', 'Duct System Overhaul'],
    invoiceDescriptions: ['AC Unit Installation - 3 Ton', 'Furnace Repair - Parts & Labor', 'Quarterly HVAC Maintenance', 'Emergency Service Call - Weekend', 'Duct Cleaning - 2000 sq ft'],
    appointmentTypes: ['Installation', 'Repair', 'Maintenance', 'Inspection', 'Emergency Service', 'Consultation']
  },
  plumbing: {
    services: ['Pipe Repair', 'Drain Cleaning', 'Water Heater Installation', 'Leak Detection', 'Toilet Repair', 'Sewer Line Service', 'Faucet Installation', 'Emergency Plumbing'],
    contactTitles: ['Property Manager', 'Homeowner', 'Facility Director', 'Restaurant Owner', 'Building Manager'],
    dealNames: ['Whole House Repiping', 'Water Heater Replacement', 'Bathroom Renovation Plumbing', 'Commercial Kitchen Plumbing', 'Sewer Line Replacement'],
    invoiceDescriptions: ['Water Heater Installation - 50 Gallon', 'Main Line Drain Cleaning', 'Leak Repair - Under Sink', 'Toilet Replacement', 'Emergency Pipe Burst Repair'],
    appointmentTypes: ['Repair', 'Installation', 'Inspection', 'Emergency Call', 'Maintenance', 'Estimate']
  },
  remodeling: {
    services: ['Kitchen Remodel', 'Bathroom Renovation', 'Basement Finishing', 'Room Addition', 'Flooring Installation', 'Cabinet Installation', 'Countertop Replacement', 'Full Home Renovation'],
    contactTitles: ['Homeowner', 'Property Investor', 'Real Estate Developer', 'Interior Designer', 'Property Flipper'],
    dealNames: ['Master Bathroom Remodel', 'Kitchen Renovation Project', 'Basement Conversion', 'Home Addition - 500 sq ft', 'Whole House Renovation'],
    invoiceDescriptions: ['Kitchen Remodel - Cabinets & Countertops', 'Bathroom Renovation - Full Service', 'Hardwood Flooring - 800 sq ft', 'Basement Finishing - Materials & Labor', 'Room Addition - Framing & Drywall'],
    appointmentTypes: ['Consultation', 'Design Meeting', 'Site Inspection', 'Progress Review', 'Final Walkthrough', 'Estimate']
  },
  handyman: {
    services: ['General Repairs', 'Drywall Repair', 'Door Installation', 'Furniture Assembly', 'Painting', 'Deck Repair', 'Gutter Cleaning', 'Minor Electrical Work'],
    contactTitles: ['Homeowner', 'Landlord', 'Property Manager', 'Tenant', 'Business Owner'],
    dealNames: ['Multi-Unit Property Repairs', 'Home Repair Package', 'Rental Property Maintenance', 'Office Repairs', 'Seasonal Home Maintenance'],
    invoiceDescriptions: ['Drywall Repair - 3 Holes', 'Door Installation - Interior', 'Furniture Assembly - 5 Items', 'Deck Repair - Boards & Staining', 'Gutter Cleaning - 2 Story Home'],
    appointmentTypes: ['Repair', 'Installation', 'Maintenance', 'Assessment', 'Follow-up', 'Emergency']
  },
  electrical: {
    services: ['Electrical Panel Upgrade', 'Outlet Installation', 'Lighting Installation', 'Ceiling Fan Installation', 'Electrical Inspection', 'Generator Installation', 'EV Charger Installation', 'Emergency Electrical'],
    contactTitles: ['Homeowner', 'Facility Manager', 'Business Owner', 'Property Developer', 'Building Superintendent'],
    dealNames: ['Panel Upgrade - 200 Amp', 'Commercial Lighting Project', 'Whole House Rewiring', 'EV Charger Installation', 'Generator Installation'],
    invoiceDescriptions: ['Electrical Panel Upgrade', 'Outlet Installation - 10 Units', 'LED Lighting Installation', 'Ceiling Fan Installation', 'Electrical Safety Inspection'],
    appointmentTypes: ['Installation', 'Inspection', 'Repair', 'Upgrade', 'Emergency Service', 'Consultation']
  },
  landscaping: {
    services: ['Lawn Maintenance', 'Tree Trimming', 'Landscape Design', 'Irrigation Installation', 'Hardscaping', 'Seasonal Cleanup', 'Mulching', 'Sod Installation'],
    contactTitles: ['Homeowner', 'Property Manager', 'HOA President', 'Business Owner', 'Estate Manager'],
    dealNames: ['Full Landscape Design', 'Irrigation System Installation', 'Backyard Hardscape Project', 'Commercial Property Maintenance', 'Seasonal Maintenance Contract'],
    invoiceDescriptions: ['Weekly Lawn Maintenance', 'Tree Trimming - 5 Trees', 'Mulch Installation - 10 Yards', 'Irrigation System Repair', 'Spring Cleanup Service'],
    appointmentTypes: ['Maintenance', 'Consultation', 'Installation', 'Seasonal Service', 'Inspection', 'Design Meeting']
  },
  cleaning: {
    services: ['House Cleaning', 'Deep Cleaning', 'Move-Out Cleaning', 'Office Cleaning', 'Carpet Cleaning', 'Window Cleaning', 'Post-Construction Cleaning', 'Janitorial Services'],
    contactTitles: ['Homeowner', 'Office Manager', 'Property Manager', 'Business Owner', 'Facility Director'],
    dealNames: ['Weekly Office Cleaning Contract', 'Move-Out Deep Clean', 'Post-Construction Cleanup', 'Recurring Home Cleaning', 'Commercial Janitorial Services'],
    invoiceDescriptions: ['Weekly House Cleaning - 3 Bedroom', 'Deep Cleaning Service', 'Carpet Cleaning - 1500 sq ft', 'Window Cleaning - 20 Windows', 'Office Cleaning - Monthly'],
    appointmentTypes: ['Regular Cleaning', 'Deep Clean', 'Move-Out Service', 'Inspection', 'Consultation', 'One-Time Service']
  },
  painting: {
    services: ['Interior Painting', 'Exterior Painting', 'Cabinet Painting', 'Deck Staining', 'Drywall Repair & Paint', 'Commercial Painting', 'Pressure Washing', 'Color Consultation'],
    contactTitles: ['Homeowner', 'Property Manager', 'Business Owner', 'Contractor', 'Real Estate Agent'],
    dealNames: ['Whole House Interior Paint', 'Exterior Painting Project', 'Commercial Office Painting', 'Cabinet Refinishing', 'Multi-Unit Property Painting'],
    invoiceDescriptions: ['Interior Painting - 3 Rooms', 'Exterior House Painting', 'Cabinet Painting - Kitchen', 'Deck Staining - 400 sq ft', 'Pressure Washing - House & Driveway'],
    appointmentTypes: ['Estimate', 'Color Consultation', 'Prep Work', 'Painting', 'Touch-up', 'Final Inspection']
  }
};

// Helper function to generate realistic sample data based on niche
const generateEntityRecords = (entityType, niche, count) => {
  const nicheData = nicheTemplates?.[niche] || nicheTemplates?.hvac;
  const records = [];

  for (let i = 0; i < count; i++) {
    let record = {};

    switch (entityType) {
      case 'contacts':
        const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emily', 'Robert', 'Lisa', 'James', 'Mary', 'William', 'Patricia', 'Richard', 'Jennifer', 'Charles', 'Linda'];
        const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas'];
        const departments = ['Sales', 'Operations', 'Management', 'Customer Service', 'Technical', 'Administration', 'Finance', 'Marketing'];
        const avatarColors = ['blue', 'green', 'purple', 'orange', 'red', 'teal', 'indigo', 'pink'];
        
        const firstName = firstNames?.[i % firstNames?.length];
        const lastName = lastNames?.[i % lastNames?.length];
        const fullName = `${firstName} ${lastName}`;
        const avatarColor = avatarColors?.[i % avatarColors?.length];
        
        record = {
          name: fullName,
          email: `${firstName?.toLowerCase()}.${lastName?.toLowerCase()}${i > 15 ? i : ''}@example.com`,
          phone: `555-${String(1000 + i)?.padStart(4, '0')}`,
          company: `${nicheData?.contactTitles?.[i % nicheData?.contactTitles?.length]} Company ${Math.floor(i / 3) + 1}`,
          title: nicheData?.contactTitles?.[i % nicheData?.contactTitles?.length],
          department: departments?.[i % departments?.length],
          avatar: `https://ui-avatars.com/api/?name=${firstName}+${lastName}&background=${avatarColor}&color=fff&size=128`,
          avatarAlt: `${fullName} profile picture`,
          lastContact: new Date(Date.now() - (Math.floor(Math.random() * 90) * 24 * 60 * 60 * 1000))?.toISOString(),
          status: ['Active', 'Inactive', 'Prospect', 'Customer']?.[i % 4],
          source: ['website', 'referral', 'cold_call', 'social_media']?.[i % 4]
        };
        break;

      case 'deals':
        record = {
          dealName: nicheData?.dealNames?.[i % nicheData?.dealNames?.length],
          value: Math.floor(Math.random() * 50000) + 5000,
          stage: ['qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost']?.[i % 5],
          probability: [25, 50, 75, 100, 0]?.[i % 5],
          expectedCloseDate: new Date(Date.now() + (i * 7 * 24 * 60 * 60 * 1000))?.toISOString(),
          contactName: `Contact ${i + 1}`,
          description: `${nicheData?.dealNames?.[i % nicheData?.dealNames?.length]} - Full service package`
        };
        break;

      case 'customers':
        record = {
          companyName: `${niche?.toUpperCase()} Customer ${i + 1}`,
          contactName: ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Williams']?.[i % 4],
          email: `customer${i + 1}@example.com`,
          phone: `555-${String(2000 + i)?.padStart(4, '0')}`,
          address: `${100 + i} Main Street`,
          city: ['Springfield', 'Riverside', 'Fairview', 'Madison']?.[i % 4],
          state: ['CA', 'TX', 'FL', 'NY']?.[i % 4],
          zipCode: `${90000 + i}`,
          customerType: ['residential', 'commercial']?.[i % 2],
          status: 'active'
        };
        break;

      case 'activities':
        record = {
          activityType: ['call', 'email', 'meeting', 'task', 'note']?.[i % 5],
          subject: `Follow up on ${nicheData?.dealNames?.[i % nicheData?.dealNames?.length]}`,
          description: `Discussion about ${nicheData?.services?.[i % nicheData?.services?.length]}`,
          status: ['completed', 'pending', 'cancelled']?.[i % 3],
          priority: ['low', 'medium', 'high']?.[i % 3],
          dueDate: new Date(Date.now() + (i * 24 * 60 * 60 * 1000))?.toISOString(),
          relatedTo: 'Contact',
          relatedId: `contact_${i + 1}`
        };
        break;

      case 'services':
        record = {
          serviceName: nicheData?.services?.[i % nicheData?.services?.length],
          description: `Professional ${nicheData?.services?.[i % nicheData?.services?.length]} service`,
          category: niche,
          price: Math.floor(Math.random() * 500) + 100,
          duration: [30, 60, 90, 120]?.[i % 4],
          isActive: true,
          requiresApproval: [true, false]?.[i % 2]
        };
        break;

      case 'organizations':
        record = {
          organizationName: `${niche?.toUpperCase()} Organization ${i + 1}`,
          industry: niche,
          size: ['1-10', '11-50', '51-200', '201-500']?.[i % 4],
          website: `https://org${i + 1}.example.com`,
          phone: `555-${String(3000 + i)?.padStart(4, '0')}`,
          email: `info@org${i + 1}.example.com`,
          address: `${200 + i} Business Blvd`,
          city: ['Los Angeles', 'Houston', 'Miami', 'New York']?.[i % 4],
          state: ['CA', 'TX', 'FL', 'NY']?.[i % 4],
          status: 'active'
        };
        break;

      case 'appointments':
        record = {
          title: `${nicheData?.appointmentTypes?.[i % nicheData?.appointmentTypes?.length]} Appointment`,
          appointmentType: nicheData?.appointmentTypes?.[i % nicheData?.appointmentTypes?.length],
          startTime: new Date(Date.now() + (i * 24 * 60 * 60 * 1000))?.toISOString(),
          endTime: new Date(Date.now() + (i * 24 * 60 * 60 * 1000) + (60 * 60 * 1000))?.toISOString(),
          location: ['Office', 'Customer Site', 'Remote', 'Phone']?.[i % 4],
          status: ['scheduled', 'completed', 'cancelled', 'no_show']?.[i % 4],
          customerName: `Customer ${i + 1}`,
          notes: `${nicheData?.appointmentTypes?.[i % nicheData?.appointmentTypes?.length]} scheduled for ${nicheData?.services?.[i % nicheData?.services?.length]}`
        };
        break;

      case 'invoices':
        record = {
          invoiceNumber: `INV-${String(1000 + i)?.padStart(5, '0')}`,
          customerName: `Customer ${i + 1}`,
          issueDate: new Date(Date.now() - (i * 7 * 24 * 60 * 60 * 1000))?.toISOString(),
          dueDate: new Date(Date.now() - (i * 7 * 24 * 60 * 60 * 1000) + (30 * 24 * 60 * 60 * 1000))?.toISOString(),
          amount: Math.floor(Math.random() * 5000) + 500,
          status: ['paid', 'pending', 'overdue', 'cancelled']?.[i % 4],
          description: nicheData?.invoiceDescriptions?.[i % nicheData?.invoiceDescriptions?.length],
          items: [
            {
              description: nicheData?.invoiceDescriptions?.[i % nicheData?.invoiceDescriptions?.length],
              quantity: 1,
              rate: Math.floor(Math.random() * 5000) + 500
            }
          ]
        };
        break;

      default:
        record = { id: i + 1, name: `Sample ${entityType} ${i + 1}` };
    }

    records?.push(record);
  }

  return records;
};

export const sampleDataService = {
  /**
   * Get all sample data sets
   */
  async getAllSets(tenantId = null) {
    try {
      let query = supabase?.from('sample_data_sets')?.select(`
          *,
          created_by_user:user_profiles!created_by(first_name, last_name, email)
        `)?.order('created_at', { ascending: false });

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(set => ({
        id: set?.id,
        tenantId: set?.tenant_id,
        name: set?.name,
        description: set?.description,
        entityType: set?.entity_type,
        recordCount: set?.record_count,
        status: set?.status,
        qualityScore: set?.quality_score,
        generationConfig: set?.generation_config,
        createdBy: set?.created_by_user,
        createdAt: set?.created_at,
        updatedAt: set?.updated_at,
        lastGeneratedAt: set?.last_generated_at
      }));
    } catch (error) {
      console.error('Get sample data sets error:', error);
      throw error;
    }
  },

  /**
   * Get entity statistics
   */
  async getEntityStats() {
    try {
      const entities = ['contacts', 'deals', 'customers', 'activities', 'services', 'organizations', 'appointments', 'invoices'];
      const stats = [];

      for (const entity of entities) {
        const { data, error } = await supabase
          ?.from('sample_data_sets')
          ?.select('record_count, quality_score, last_generated_at')
          ?.eq('entity_type', entity)
          ?.order('created_at', { ascending: false })
          ?.limit(1)
          ?.single();

        stats?.push({
          entityType: entity,
          recordCount: data?.record_count || 0,
          qualityScore: data?.quality_score || 0,
          lastGeneratedAt: data?.last_generated_at || null,
          hasData: !!data
        });
      }

      return stats;
    } catch (error) {
      console.error('Get entity stats error:', error);
      throw error;
    }
  },

  /**
   * Get all templates
   */
  async getAllTemplates(entityType = null) {
    try {
      let query = supabase?.from('sample_data_templates')?.select('*')?.eq('is_active', true)?.order('entity_type', { ascending: true });

      if (entityType) {
        query = query?.eq('entity_type', entityType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(template => ({
        id: template?.id,
        entityType: template?.entity_type,
        templateName: template?.template_name,
        templateDescription: template?.template_description,
        scenario: template?.scenario,
        templateData: template?.template_data,
        isSystemTemplate: template?.is_system_template,
        isActive: template?.is_active,
        createdAt: template?.created_at
      }));
    } catch (error) {
      console.error('Get templates error:', error);
      throw error;
    }
  },

  /**
   * Get operations log
   */
  async getOperations(tenantId = null, limit = 50) {
    try {
      let query = supabase?.from('sample_data_operations')?.select(`
          *,
          performed_by_user:user_profiles!performed_by(first_name, last_name, email)
        `)?.order('started_at', { ascending: false })?.limit(limit);

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(op => ({
        id: op?.id,
        tenantId: op?.tenant_id,
        operationType: op?.operation_type,
        entityType: op?.entity_type,
        recordsAffected: op?.records_affected,
        status: op?.status,
        errorMessage: op?.error_message,
        operationDetails: op?.operation_details,
        performedBy: op?.performed_by_user,
        startedAt: op?.started_at,
        completedAt: op?.completed_at,
        durationMs: op?.duration_ms
      }));
    } catch (error) {
      console.error('Get operations error:', error);
      throw error;
    }
  },

  /**
   * Generate sample data for entity
   */
  async generateSampleData(entityType, config = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const startTime = Date.now();
      const niche = config?.niche || 'hvac';
      const nicheData = nicheTemplates?.[niche] || nicheTemplates?.hvac;
      const recordCount = config?.count || 50;

      // Create operation log with niche information
      const operationData = toSnakeCase({
        tenantId: config?.tenantId || null,
        operationType: 'generate',
        entityType: entityType,
        status: 'in_progress',
        operationDetails: { ...config, niche, nicheData },
        performedBy: user?.id
      });

      const { data: operation, error: opError } = await supabase
        ?.from('sample_data_operations')
        ?.insert([operationData])
        ?.select()
        ?.single();

      if (opError) throw opError;

      // Create or update sample data set record
      const setData = {
        tenant_id: config?.tenantId || null,
        name: `${entityType} Sample Data - ${niche?.toUpperCase()}`,
        entity_type: entityType,
        record_count: recordCount,
        status: 'active',
        quality_score: 85,
        generation_config: { ...config, niche },
        created_by: user?.id,
        last_generated_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      const { data: dataSet, error: setError } = await supabase
        ?.from('sample_data_sets')
        ?.upsert(setData, {
          onConflict: 'entity_type,tenant_id'
        })
        ?.select()
        ?.single();

      if (setError) throw setError;

      // Generate actual sample records
      const sampleRecords = generateEntityRecords(entityType, niche, recordCount);

      // Delete existing records for this set to avoid duplicates
      await supabase
        ?.from('sample_data_records')
        ?.delete()
        ?.eq('set_id', dataSet?.id);

      // Insert new sample records into sample_data_records table
      const recordsToInsert = sampleRecords?.map(record => ({
        set_id: dataSet?.id,
        record_data: record,
        created_at: new Date()?.toISOString()
      }));

      const { error: insertError } = await supabase
        ?.from('sample_data_records')
        ?.insert(recordsToInsert);

      if (insertError) throw insertError;

      const endTime = Date.now();

      // Update operation status
      const { error: updateError } = await supabase
        ?.from('sample_data_operations')
        ?.update({
          status: 'completed',
          records_affected: recordCount,
          completed_at: new Date()?.toISOString(),
          duration_ms: endTime - startTime
        })
        ?.eq('id', operation?.id);

      if (updateError) throw updateError;

      return { success: true, operationId: operation?.id, recordsGenerated: recordCount };
    } catch (error) {
      console.error('Generate sample data error:', error);
      throw error;
    }
  },

  /**
   * Reset sample data for entity
   */
  async resetSampleData(entityType, tenantId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const startTime = Date.now();

      // Get the sample data set to find records to delete
      let query = supabase
        ?.from('sample_data_sets')
        ?.select('id')
        ?.eq('entity_type', entityType);

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data: sets } = await query;

      // Delete all records associated with these sets
      if (sets && sets?.length > 0) {
        const setIds = sets?.map(s => s?.id);
        await supabase
          ?.from('sample_data_records')
          ?.delete()
          ?.in('set_id', setIds);
      }

      const operationData = toSnakeCase({
        tenantId: tenantId,
        operationType: 'reset',
        entityType: entityType,
        status: 'completed',
        recordsAffected: 0,
        performedBy: user?.id,
        completedAt: new Date()?.toISOString(),
        durationMs: Date.now() - startTime
      });

      const { error } = await supabase
        ?.from('sample_data_operations')
        ?.insert([operationData]);

      if (error) throw error;

      // Delete sample data set record
      const { error: deleteError } = await supabase
        ?.from('sample_data_sets')
        ?.delete()
        ?.eq('entity_type', entityType);

      if (deleteError && deleteError?.code !== 'PGRST116') throw deleteError;

      return { success: true };
    } catch (error) {
      console.error('Reset sample data error:', error);
      throw error;
    }
  },

  /**
   * Clear ALL sample data across all entities
   * Used when switching niches or starting fresh
   */
  async clearAllSampleData(tenantId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const startTime = Date.now();

      // Get all sample data sets
      let query = supabase
        ?.from('sample_data_sets')
        ?.select('id, entity_type');

      if (tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data: sets, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      let totalRecordsDeleted = 0;

      // Delete all records associated with these sets
      if (sets && sets?.length > 0) {
        const setIds = sets?.map(s => s?.id);
        
        // Delete all sample data records
        const { error: recordsError } = await supabase
          ?.from('sample_data_records')
          ?.delete()
          ?.in('set_id', setIds);

        if (recordsError) throw recordsError;

        totalRecordsDeleted = sets?.length;

        // Delete all sample data sets
        const { error: setsError } = await supabase
          ?.from('sample_data_sets')
          ?.delete()
          ?.in('id', setIds);

        if (setsError) throw setsError;
      }

      // Log the operation
      const operationData = toSnakeCase({
        tenantId: tenantId,
        operationType: 'clear_all',
        entityType: null,
        status: 'completed',
        recordsAffected: totalRecordsDeleted,
        performedBy: user?.id,
        completedAt: new Date()?.toISOString(),
        durationMs: Date.now() - startTime
      });

      const { error: opError } = await supabase
        ?.from('sample_data_operations')
        ?.insert([operationData]);

      if (opError) throw opError;

      return { 
        success: true, 
        recordsDeleted: totalRecordsDeleted,
        message: `Successfully cleared all sample data (${totalRecordsDeleted} sets removed)` 
      };
    } catch (error) {
      console.error('Clear all sample data error:', error);
      throw error;
    }
  },

  /**
   * Export sample data
   */
  async exportSampleData(entityType, tenantId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const startTime = Date.now();

      const operationData = toSnakeCase({
        tenantId: tenantId,
        operationType: 'export',
        entityType: entityType || 'all',
        status: 'completed',
        recordsAffected: 0,
        performedBy: user?.id,
        completedAt: new Date()?.toISOString(),
        durationMs: Date.now() - startTime
      });

      const { error } = await supabase
        ?.from('sample_data_operations')
        ?.insert([operationData]);

      if (error) throw error;

      // In production, this would generate and download actual export file
      return { success: true, message: 'Export initiated' };
    } catch (error) {
      console.error('Export sample data error:', error);
      throw error;
    }
  },

  /**
   * Bulk generate all entities
   */
  async bulkGenerateAll(config = {}) {
    try {
      const entities = ['contacts', 'customers', 'appointments', 'invoices', 'services'];
      const results = [];

      for (const entity of entities) {
        try {
          const result = await this.generateSampleData(entity, {
            ...config,
            count: config?.count || 50,
            niche: config?.niche || 'hvac'
          });
          results?.push({ entity, success: true, ...result });
        } catch (error) {
          results?.push({ entity, success: false, error: error?.message });
        }
      }

      return { success: true, results };
    } catch (error) {
      console.error('Bulk generate error:', error);
      throw error;
    }
  },

  /**
   * Get niche templates
   */
  getNicheTemplates() {
    return nicheTemplates;
  },

  /**
   * Get available niches
   */
  getAvailableNiches() {
    return Object.keys(nicheTemplates);
  },

  /**
   * Get sample data by entity type
   * @param {string} entityType - The entity type (contacts, deals, customers, etc.)
   * @param {string} tenantId - Optional tenant ID filter
   * @returns {Promise<Array>} Sample data records
   */
  async getSampleDataByEntity(entityType, tenantId = null) {
    try {
      // First, get the active sample data set for this entity type
      let setQuery = supabase?.from('sample_data_sets')?.select('id, entity_type, status, generation_config')?.eq('entity_type', entityType)?.eq('status', 'active')?.order('last_generated_at', { ascending: false })?.limit(1);

      // Only filter by tenant_id if explicitly provided and not null
      // This allows sample data to be retrieved regardless of tenant for testing
      if (tenantId) {
        setQuery = setQuery?.eq('tenant_id', tenantId);
      }

      const { data: sets, error: setError } = await setQuery;

      if (setError) throw setError;
      if (!sets || sets?.length === 0) {
        // If no data found with tenant filter, try without tenant filter
        if (tenantId) {
          const { data: fallbackSets, error: fallbackError } = await supabase?.from('sample_data_sets')?.select('id, entity_type, status, generation_config')?.eq('entity_type', entityType)?.eq('status', 'active')?.order('last_generated_at', { ascending: false })?.limit(1);

          if (fallbackError) throw fallbackError;
          if (!fallbackSets || fallbackSets?.length === 0) {
            return []; // No sample data set exists for this entity
          }
          
          const dataSet = fallbackSets?.[0];
          
          // Get records for fallback set
          const { data: records, error: recordsError } = await supabase?.from('sample_data_records')?.select('id, set_id, record_data, created_at')?.eq('set_id', dataSet?.id)?.order('created_at', { ascending: true });

          if (recordsError) throw recordsError;

          return records?.map(record => ({
            id: record?.id,
            setId: record?.set_id,
            entityType: dataSet?.entity_type,
            ...record?.record_data
          })) || [];
        }
        return []; // No sample data set exists for this entity
      }

      const dataSet = sets?.[0];

      // Now get all records for this set
      const { data: records, error: recordsError } = await supabase?.from('sample_data_records')?.select('id, set_id, record_data, created_at')?.eq('set_id', dataSet?.id)?.order('created_at', { ascending: true });

      if (recordsError) throw recordsError;

      // Transform data and extract record_data
      return records?.map(record => ({
        id: record?.id,
        setId: record?.set_id,
        entityType: dataSet?.entity_type,
        ...record?.record_data // Spread the actual data fields
      })) || [];
    } catch (error) {
      console.error(`Get sample data for ${entityType} error:`, error);
      throw error;
    }
  }
};

export default sampleDataService;
